import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
} from '@mui/material';
import {
  HomeOutlined as HomeIcon,
  CampaignOutlined as CampaignIcon,
  SettingsOutlined as SettingsIcon,
  HelpOutlineOutlined as HelpIcon,
  InfoOutlined as InfoIcon,
  AdminPanelSettingsOutlined as AdminIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AppColors } from '../theme/colors';
import { SidebarBrand } from './SidebarBrand';
import { PostTypeSelectDialog, PostType } from './PostTypeSelectDialog';
import { useAuth, useCompany } from '../hooks/useAuth';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { company } = useCompany(user?.uid || null);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);

  const handleNavigation = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleHomeClick = () => {
    onClose();
    // ウェルカムモーダルを強制表示するためのフラグを設定
    try {
      sessionStorage.removeItem('welcome_dismissed');
      sessionStorage.setItem('welcome_force_open', 'true');
    } catch (e) {
      console.error('Failed to set welcome flags:', e);
    }
    navigate('/home');
  };

  const handleNewPostClick = () => {
    // ドロワーを閉じてから選択ダイアログを表示
    onClose();
    setTypeDialogOpen(true);
  };

  const handleSelectType = (type: PostType) => {
    setTypeDialogOpen(false);
    navigate(`/post/new?type=${type}`);
  };

  return (
    <>
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          zIndex: 1300, // ヘッダー（z-index: 1100）より上に表示
          '& .MuiDrawer-paper': {
            width: '65%',
            bgcolor: 'white',
            borderTopRightRadius: 16,
            borderBottomRightRadius: 16,
          },
        }}
      >
        <Box sx={{ height: 56, bgcolor: AppColors.primary }} />
        <List>
          <ListItemButton onClick={handleHomeClick}>
            <ListItemIcon>
              <HomeIcon sx={{ color: AppColors.primary }} />
            </ListItemIcon>
            <ListItemText primary="ホーム" />
          </ListItemButton>
          <ListItemButton onClick={handleNewPostClick}>
            <ListItemIcon>
              <CampaignIcon sx={{ color: AppColors.primary }} />
            </ListItemIcon>
            <ListItemText primary="新規投稿" />
          </ListItemButton>
          <Divider />
          <ListItemButton onClick={() => handleNavigation('/settings')}>
            <ListItemIcon>
              <SettingsIcon sx={{ color: AppColors.primary }} />
            </ListItemIcon>
            <ListItemText primary="設定" />
          </ListItemButton>
          <ListItemButton onClick={() => handleNavigation('/help')}>
            <ListItemIcon>
              <HelpIcon sx={{ color: AppColors.primary }} />
            </ListItemIcon>
            <ListItemText primary="ヘルプ" />
          </ListItemButton>
          <ListItemButton onClick={() => handleNavigation('/about-app')}>
            <ListItemIcon>
              <InfoIcon sx={{ color: AppColors.primary }} />
            </ListItemIcon>
            <ListItemText primary="アプリについて" />
          </ListItemButton>
          <Divider />
          <ListItemButton onClick={() => handleNavigation('/admin-login')}>
            <ListItemIcon>
              <AdminIcon sx={{ color: AppColors.primary }} />
            </ListItemIcon>
            <ListItemText primary="管理画面ログイン" />
          </ListItemButton>
        </List>

        <SidebarBrand />
      </Drawer>

      <PostTypeSelectDialog
        open={typeDialogOpen}
        onClose={() => setTypeDialogOpen(false)}
        onSelect={handleSelectType}
        industryType={company?.industryType}
      />
    </>
  );
};
